import { SDS_FONT_FAMILY_PRIMARY, SDS_FONT_WEIGHT_REGULAR, SDS_FONT_WEIGHT_MEDIUM, SDS_FONT_SIZE_BODY_SMALL, SDS_FONT_LINEHEIGHT_BODY_SMALL, SDS_FONT_SIZE_BODY_MEDIUM, SDS_FONT_LINEHEIGHT_BODY_MEDIUM, SDS_FONT_SIZE_BODY_LARGE, SDS_FONT_LINEHEIGHT_BODY_LARGE, SDS_FONT_SIZE_HEADING_SMALL, SDS_FONT_LINEHEIGHT_HEADING_SMALL, SDS_FONT_SIZE_HEADING_MEDIUM, SDS_FONT_LINEHEIGHT_HEADING_MEDIUM, SDS_FONT_SIZE_BODY_XSMALL, SDS_FONT_LINEHEIGHT_BODY_XSMALL } from '@s-group/design-system-tokens/web/tokens/font.es6.js';
import { SDS_MISC_ALPHA_XXSMALL } from '@s-group/design-system-tokens/web/tokens/misc.es6.js';
import { SDS_MOTION_DURATION_DEFAULT, SDS_MOTION_EASING_DEFAULT } from '@s-group/design-system-tokens/web/tokens/motion.es6.js';
import { SDS_SIZE_BORDER_SMALL, SDS_SIZE_XXSMALL, SDS_SIZE_XSMALL, SDS_SIZE_SMALL, SDS_SIZE_XXXXSMALL, SDS_SIZE_XXXSMALL, SDS_SIZE_LARGE, SDS_SIZE_BORDER_RADIUS_XXXSMALL, SDS_SIZE_BORDER_RADIUS_NONE, SDS_SIZE_BORDER_LARGE } from '@s-group/design-system-tokens/web/tokens/size.es6.js';
import { SDS_SPACE_XXXSMALL, SDS_SPACE_XXXXSMALL } from '@s-group/design-system-tokens/web/tokens/space.es6.js';
import { rgba, math } from 'polished';
import { css } from 'styled-components';
import { safariRoundedOutlinePolyfill } from '../common/polyfill.js';
import { scaledIcon, bordered } from '../common/util.js';
import { direction, boxSide } from './constants.js';

const backgroundColor = ({ alternative, theme, }) => alternative
    ? theme.colors.SDS_BRAND_COLOR_BACKGROUND_WEAK_GREY
    : 'transparent';
const borderBottom = ({ alternative, border, theme, }) => alternative || !border
    ? '0'
    : `${SDS_SIZE_BORDER_SMALL} solid ${theme.colors.SDS_BRAND_COLOR_BORDER_WEAK_GREY}`;
const borderMargin = ({ alignment, sizing, toggle }) => alignment === 'flush' &&
    sizing &&
    toggle &&
    padding({ sizing, toggle })[direction.HORIZONTAL];
const headerFontMetrics = {
    // sizing: [font-size, line-height]
    xsmall: [SDS_FONT_SIZE_BODY_SMALL, SDS_FONT_LINEHEIGHT_BODY_SMALL],
    small: [SDS_FONT_SIZE_BODY_MEDIUM, SDS_FONT_LINEHEIGHT_BODY_MEDIUM],
    medium: [SDS_FONT_SIZE_BODY_LARGE, SDS_FONT_LINEHEIGHT_BODY_LARGE],
    large: [SDS_FONT_SIZE_HEADING_SMALL, SDS_FONT_LINEHEIGHT_HEADING_SMALL],
    xlarge: [SDS_FONT_SIZE_HEADING_MEDIUM, SDS_FONT_LINEHEIGHT_HEADING_MEDIUM],
};
const panelFontMetrics = {
    // sizing: [font-size, line-height]
    xsmall: [SDS_FONT_SIZE_BODY_XSMALL, SDS_FONT_LINEHEIGHT_BODY_XSMALL],
    small: [SDS_FONT_SIZE_BODY_SMALL, SDS_FONT_LINEHEIGHT_BODY_SMALL],
    medium: [SDS_FONT_SIZE_BODY_MEDIUM, SDS_FONT_LINEHEIGHT_BODY_MEDIUM],
    large: [SDS_FONT_SIZE_BODY_MEDIUM, SDS_FONT_LINEHEIGHT_BODY_MEDIUM],
    xlarge: [SDS_FONT_SIZE_BODY_LARGE, SDS_FONT_LINEHEIGHT_BODY_LARGE],
};
const font = (fontMetrics, fontWeight) => ({ sizing }) => sizing &&
    [fontMetrics[sizing]]
        .map(([fontSize, lineHeight]) => `${fontWeight} ${fontSize} / ${lineHeight} '${SDS_FONT_FAMILY_PRIMARY}'`)
        .join('');
const iconPadding = ({ sizing, toggle }) => toggle && sizing
    ? {
        after: {
            xsmall: SDS_SIZE_XXSMALL,
            small: SDS_SIZE_XSMALL,
            medium: SDS_SIZE_XSMALL,
            large: SDS_SIZE_XSMALL,
            xlarge: SDS_SIZE_SMALL,
        },
        before: {
            xsmall: SDS_SIZE_XXXXSMALL,
            small: SDS_SIZE_XXXSMALL,
            medium: SDS_SIZE_XXXSMALL,
            large: SDS_SIZE_XXXSMALL,
            xlarge: SDS_SIZE_XXSMALL,
        },
    }[toggle][sizing]
    : 0;
function iconSize({ sizing }) {
    if (sizing) {
        return scaledIcon(headerFontMetrics[sizing]).iconSize;
    }
}
function iconMargin({ sizing }) {
    if (sizing) {
        return scaledIcon(headerFontMetrics[sizing]).iconMargin;
    }
}
function padding({ sizing, toggle }) {
    if (sizing && toggle) {
        return {
            after: {
                // size: [vertical, horizontal]
                xsmall: [SDS_SIZE_XSMALL, SDS_SIZE_XSMALL],
                small: [SDS_SIZE_SMALL, SDS_SIZE_SMALL],
                medium: [SDS_SIZE_SMALL, SDS_SIZE_SMALL],
                large: [SDS_SIZE_SMALL, SDS_SIZE_SMALL],
                xlarge: [SDS_SIZE_LARGE, SDS_SIZE_LARGE],
            },
            before: {
                // size: [vertical, horizontal]
                xsmall: [SDS_SIZE_XSMALL, SDS_SIZE_XXXSMALL],
                small: [SDS_SIZE_SMALL, SDS_SIZE_XXSMALL],
                medium: [SDS_SIZE_SMALL, SDS_SIZE_XXSMALL],
                large: [SDS_SIZE_SMALL, SDS_SIZE_XXSMALL],
                xlarge: [SDS_SIZE_LARGE, SDS_SIZE_XSMALL],
            },
        }[toggle][sizing];
    }
}
const panelLeftIndent = ({ sizing, toggle }) => toggle === 'before' && sizing
    ? math(`${iconSize({ sizing })} + 2 * ${iconMargin({
        sizing,
    })} + ${iconPadding({ sizing, toggle })}`)
    : 0;
const headerLeftIndentCompensation = ({ sizing, toggle }) => toggle === 'before' && sizing
    ? math(`-1 * (${panelLeftIndent({ sizing, toggle })} + ${padding({ sizing, toggle })[direction.HORIZONTAL]})`)
    : undefined;
// Used to increase the header click area, overlaps with `panelPadding` due
// negative margins
const headerPadding = ({ sizing, toggle }) => sizing && toggle && padding({ sizing, toggle }).join(' ');
// Same as `headerPadding` but negative
const headerMargin = ({ sizing, toggle }) => sizing &&
    toggle &&
    padding({ sizing, toggle })
        .map(value => math(`-1 * ${value}`))
        .join(' ');
const panelPadding = ({ alternative, border, sizing, toggle, }) => {
    const hasBorder = (sideIndex) => !alternative && border && sideIndex === boxSide.BOTTOM;
    const hasIndent = (sideIndex) => toggle === 'before' && sideIndex === boxSide.LEFT;
    return (sizing &&
        toggle &&
        padding({ sizing, toggle })
            // [vertical, horizontal] -> [top, right, bottom, left]
            .flatMap((dimension, index, array) => array)
            .map((sidePadding, sideIndex) => hasBorder(sideIndex)
            ? bordered(sidePadding)
            : hasIndent(sideIndex)
                ? math(`${sidePadding} + ${panelLeftIndent({
                    toggle,
                    sizing,
                })}`)
                : sidePadding)
            .join(' '));
};
const flushMargin = ({ alignment, sizing, toggle }) => alignment === 'flush' && sizing && toggle
    ? math(`-1 * ${padding({ sizing, toggle })[direction.HORIZONTAL]}`)
    : 0;
const rounding = ({ rounding }) => rounding === 'small'
    ? SDS_SIZE_BORDER_RADIUS_XXXSMALL
    : SDS_SIZE_BORDER_RADIUS_NONE;
const outlineColor = ({ theme }) => typeof theme.colors.SDS_BRAND_COLOR_BORDER_STRONG_PRIMARY === 'string'
    ? theme.colors.SDS_BRAND_COLOR_BORDER_STRONG_PRIMARY
    : 'none';
const outlineOffset = SDS_SPACE_XXXXSMALL;
const outlineWidth = SDS_SIZE_BORDER_LARGE;
const CollapsibleStyle = css `
    align-self: flex-start;
    background-color: ${backgroundColor};
    border-radius: ${rounding};
    flex: 1;
    font: ${font(panelFontMetrics, SDS_FONT_WEIGHT_REGULAR)};
    margin: 0 ${flushMargin};
    padding: ${panelPadding};
    position: relative;

    &::after {
        border-bottom: ${borderBottom};
        bottom: 0;
        content: '';
        left: 0;
        margin: 0 ${borderMargin};
        position: absolute;
        right: 0;
    }

    summary {
        border-radius: ${rounding};
        color: ${({ theme }) => theme.colors.SDS_BRAND_COLOR_TEXT_DEFAULT_GREY};
        cursor: pointer;
        display: flex;
        flex-direction: ${({ toggle }) => toggle === 'before' ? 'row-reverse' : 'row'};
        font: ${font(headerFontMetrics, SDS_FONT_WEIGHT_MEDIUM)};
        gap: ${iconPadding};
        list-style: none;
        margin: ${headerMargin};
        margin-left: ${headerLeftIndentCompensation};
        padding: ${headerPadding};

        &:hover,
        &:active {
            background: ${({ theme }) => rgba(theme.colors.SDS_BRAND_COLOR_TEXT_DEFAULT_GREY, SDS_MISC_ALPHA_XXSMALL)};
        }

        &:focus-visible {
            box-shadow: 0 0 0 ${outlineOffset}
                ${({ theme }) => theme.colors.SDS_BRAND_COLOR_BACKGROUND_DEFAULT_GREY};
            outline: ${outlineWidth} solid
                ${({ theme }) => theme.colors.SDS_BRAND_COLOR_BORDER_STRONG_PRIMARY};
            outline-offset: ${outlineOffset};
            ${props => safariRoundedOutlinePolyfill({
    borderRadius: rounding(props),
    outlineColor: outlineColor(props),
    outlineOffset,
    outlineWidth,
})};
        }

        &::-webkit-details-marker {
            display: none;
        }
    }

    .header-wrapper {
        display: flex;
        flex: 1;

        > h2,
        > h3,
        > h4,
        > h5,
        > h6 {
            color: inherit;
            font: inherit;
            margin: inherit;
        }
    }

    .leading-icon,
    .toggle-icon {
        box-sizing: content-box;
        height: ${iconSize};
        margin: ${iconMargin};
        width: ${iconSize};

        svg {
            height: auto;
            width: auto;
        }
    }

    .toggle-icon {
        svg {
            transform: ${({ toggle }) => toggle === 'before' ? 'rotate(-90deg)' : 'rotate(0deg)'};
            transition: transform ${SDS_MOTION_DURATION_DEFAULT}
                cubic-bezier(${SDS_MOTION_EASING_DEFAULT});
        }
    }

    &[open] {
        summary {
            /* Ensure focus outline is not overlapping with the panel content */
            margin-bottom: ${SDS_SPACE_XXXSMALL};
        }

        .toggle-icon svg {
            transform: ${({ toggle }) => toggle === 'before' ? 'rotate(0deg)' : 'rotate(-180deg)'};
        }
    }
`;

export { CollapsibleStyle };
//# sourceMappingURL=Collapsible.styles.js.map
